<template>
  <div class="flex flex-col align-center px-10 relative h-full" v-if="project.title">

    <h1 class="sm:text-3xl lg:text-4xl font-bold title-font text-default mb-4 leading-4">
      Paramètres du projet
    </h1>

    <h2 v-if="project.title" class="mt-5">"{{ project.title }}"</h2>

    <!-- Chargement -->
    <skeleton-loader-vue
      v-else
      type="rect"
      :width="300"
      :height="20"
      animation="wave"
      class="my-5"
      rounded
    />

    <!-- Retour arrière -->
    <Button
      class="absolute right-0"
      icon="mdi-arrow-left"
      label="Retour"
      @click="$router.back()"
    />

    <form
      id="createSnippet" @submit.prevent="saveProject"
      class="flex flex-wrap justify-between gap-8 h-full"
    >
      <div class="flex flex-col">

        <!-- Titre du projet -->
        <div class="p-field text-left">
          <label for="titre">Nom du projet</label>
          <InputText
            id="title"
            v-model="project.title"
            required="true"
            autofocus
            :class="{'p-invalid': !project.title}"
          />
          <small v-if="errors.title" class="p-invalid text-red">
            {{ parseErrorRule(errors.title, "Le titre") }}
          </small>
        </div>

        <!-- Description du projet -->
        <div class="p-field">
          <label for="description">Description du projet (facultatif)</label>
          <Textarea
            id="title"
            placeholder="Ce projet peut ne pas avoir de description"
            v-model="project.description"
            rows="5"
          />
          <small v-if="errors.title" class="p-invalid text-red">
            {{ parseErrorRule(errors.description, "La description") }}
          </small>
        </div>

        <!-- Mode examen -->
        <div v-if="project.type === 'examen'">
          <h2 class="mb-1">Mode examen</h2>

          <div class="p-field-checkbox flex flex-wrap align-center gap-2">
            <div class="font-light block">
              En définissant une date de fermeture et ou une date d’ouverture, le code de votre projet
              ne sera éditable par les membres que dans la plage horaire disponible
            </div>

            <div
              class="grid w-full gap-2"
              v-if="project.hasAccessPeriod"
            >
              <label for="startDate">Date de début : </label>
              <Calendar
                class="max-w-lg"
                v-model="project.startDate"
                :min-date="new Date()"
                :numberOfMonths="2"
                dateFormat="dd/mm/yy"
                show-time
                show-icon
              />
            </div>
            <div
              class="grid w-full gap-2"
              v-if="project.hasAccessPeriod"
            >
              <label for="endDate">Date de fin : </label>
              <Calendar
                class="max-w-lg"
                v-model="project.endDate"
                :min-date="minEndDate"
                :numberOfMonths="2"
                dateFormat="dd/mm/yy"
                show-time
                show-icon
              />
            </div>
          </div>
        </div>

        <!-- Membres -->
        <div class="mb-8">
          <h3 class="mb-1">Membres du projet</h3>
          <div class="font-light max-w-lg mb-2">Ajoutez ici les membres de votre projet via leur
            adresse mail. S’ils n’ont pas encore de compte, nous leur enverrons une invitation
            pour rejoindre votre projet !
          </div>
          <div v-if="project.membersInvitations.length" class="my-3">
            <p>Invitations en attente :
              <span v-for="(memberInvitation, key) in project.membersInvitations" :key="key">
                {{memberInvitation}}<span v-if="key != project.membersInvitations.length - 1">,</span></span>
            </p>
          </div>
          <Chips
            id="members-emails" class="p-chips-multiline"
            v-model="project.membersEmail" :allowDuplicate="false"
            placeholder="membre@gmail.com"
            @add="addMember"
            @remove="removeMember"
            separator=","
            addOnBlur
          />
        </div>

        <!-- Administrateurs -->
        <div class="mb-3">
          <h3 class="mb-2">Administrateurs du projet</h3>
          <div v-if="project.adminsInvitations.length" class="my-3">
            <p>Invitations en attente :
              <span v-for="(adminInvitation, key) in project.adminsInvitations" :key="key">
                {{adminInvitation}}<span v-if="key != project.adminsInvitations.length - 1">,</span></span>
            </p>
          </div>
          <Chips
            id="admin-emails" class="p-chips-multiline"
            v-model="project.adminsEmail" :allowDuplicate="false"
            placeholder="admin@gmail.com"
            @add="(event) => addMember(event, true)"
            @remove="removeMember"
            separator=","
            addOnBlur
          />
        </div>

        <!-- Sauvegarde -->
        <div class="block w-full my-5">
          <Button
            class="btn-green"
            :is-loading="isProjectBeingSaved" @click="saveProject"
            icon="mdi-content-save"
            :icon-size="24"
            label="Sauvegarder"
          />
        </div>

        <br class="my-5">

        <!-- Archivage -->
        <div>
          <h2 class="mb-3">Archivage</h2>
          <div class="max-w-lg mb-3">
            Archiver votre projet verrouillera l’édition à tous vos membres mais restera visible
            aux membres.
          </div>
          <div class="p-field-checkbox flex flex-wrap align-center gap-2 mt-4">
            <Button
              @click="archiveProject()"
              label="Archiver le projet"
              class="p-button-rounded btn-red"
              :is-loading="isProjectBeingArchived"
            />
          </div>
        </div>

      </div>

    </form>
  </div>
</template>

<script>
import axios from "axios"
import moment from "moment";
import { mapGetters } from "vuex";
import LoadingSpinner from "../../components/Extra/LoadingSpinner"

export default {
  name: 'SnippetSettings',
  components: {
    LoadingSpinner
  },
  data: () => ({
    selectedType: null,
    type: [
      { name: 'teach' },
      { name: 'examen' }
    ],
    errors: {},
    project: {
      title: null,
      description: "",
      type: "",
      hasAccessPeriod: false,
      startDate: null,
      endDate: null,
      membersEmail: [],
      membersInvitations: [],
      adminsEmail: [],
      adminsInvitations: []
    },
    isProjectBeingSaved: false,
    isProjectBeingArchived: false,
  }),
  computed: {
    ...mapGetters("projects", {
      currentProject: "getCurrent",
    }),
    minEndDate() {
      // Used to keep user from choosing a end date before the start date
      return moment(this.project.startDate).toDate()
    }
  },
  async mounted() {
    await this.fetchProject()
    this.selectedType = {name: this.project.type }

    const userId = this.$store.state.auth.id
    const userIsCreator = userId === this.project.creator_user_id
    if (!userIsCreator) {
      const currentUser = this.project.members.find(element => element.id = userId);
      if(!currentUser.pivot.is_admin) {
        await this.$router.push({ name: "401 Non autorisé" });
      }
    }
  },
  methods: {
    async fetchProject() {
      try {
        if (this.$route.params.idProject !== this.currentProject.id) {
          await this.$store.dispatch('projects/fetchCurrentProject', this.$route.params.idProject)
        }

        // Members list
        const members = this.currentProject.members.filter(member => {
          return !member.pivot.is_admin
        }).map(member => member.email)

        // Invitations list
        const invitationsMembers = this.currentProject.invitations.filter(invitation => {
          return !invitation.is_admin
        }).map(invitation => invitation.email)

        // Admins list
        const admins = this.currentProject.members.filter(member => {
          return member.pivot.is_admin
        }).map(member => member.email)

        // Invitations admins list
        const adminsInvitations = this.currentProject.invitations.filter(invitation => {
          return invitation.is_admin
        }).map(invitation => invitation.email)

        this.project = {
          ...this.currentProject,
          membersEmail: members,
          membersInvitations: invitationsMembers,
          adminsEmail: admins,
          adminsInvitations: adminsInvitations
        }
      } catch (error) {
        if (error.response.status === 404) {
          await this.$router.push('/erreur/404')
        }
      }
    },
    // getMembersEmail(project, isAdmin = false) {
    //   const membersEmail = project.members.filter(member => !!member.pivot.is_admin === isAdmin).map(member => member.email)
    //   const invitedMembersEmail = project.invitations.filter(invitedMember => !!invitedMember.is_admin === isAdmin).map(member => member.email)
    //
    //   return [...membersEmail, ...invitedMembersEmail]
    // },
    // TODO edit when archiving is implemented
    async archiveProject() {
      this.$confirm.require({
        message: 'Êtes-vous sûr de vouloir archiver ce projet ? Vous pourrez toujours le restaurer depuis la page \"Projets archivés\"',
        header: 'Confirmation de l’archivage',
        icon: 'pi pi-exclamation-triangle',
		acceptLabel: 'Oui',
		rejectLabel: 'Non',
        accept: async () => {
          this.isProjectBeingArchived = true
          await axios.delete(`/projects/${this.$route.params.idProject}/archive`)
          this.isProjectBeingArchived = false

          this.$store.dispatch('projects/fetchRecents')
          this.$toast.add({
            severity: 'success',
            summary: 'Projet archivé',
            detail: 'Le projet a été archivé avec succès',
            life: 3000
          })
          this.$router.push('/dashboard')
        }
      })
    },
    async saveProject() {
      const newStartDate = moment(this.project.startDate).format('YYYY-MM-DD H:mm:ss')
      const newEndDate = moment(this.project.endDate).format('YYYY-MM-DD H:mm:ss')

      try {
        this.isProjectBeingSaved = true
        await axios.put(`/projects/${this.$route.params.idProject}`, {
          title: this.project.title,
          description: this.project.description,
          type: this.selectedType.name,
          ...this.project.type != "team"
            ? {
              start_date: newStartDate,
              end_date: newEndDate,
            }
            : {}
        })
        this.$toast.add({
          severity: 'success',
          summary: 'Projet sauvegardé',
          detail: `Le projet a été sauvegardé avec succès`,
          life: 3000
        })
      } catch ({ response }) {
        this.errors = response.data.errors
      }
      finally {
        this.isProjectBeingSaved = false
      }
    },
    async addMember({ value }, isAdmin = false) {
      const newMemberEmail = value[value.length - 1].toLowerCase()

      // Validate email format
      const isValidEmail = newMemberEmail.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
      if (!isValidEmail) {
        isAdmin
          ? this.project.adminsEmail.pop()
          : this.project.membersEmail.pop()

        return this.$toast.add({
          severity: 'error',
          summary: 'Adresse email invalide',
          detail: `L’adresse email ${newMemberEmail} n’est pas valide`,
          life: 3000
        })
      }

      try {
        await axios.post(`/projects/${this.$route.params.idProject}/member/add`, {
          email: newMemberEmail,
          is_admin: isAdmin,
        })
        this.$toast.add({
          severity: 'success',
          summary: 'Membre ajouté',
          detail: `Le membre a été ajouté au projet !`,
          life: 3000
        })
        await this.fetchProject()
      } catch ({ response }) {
        this.$toast.add({
          severity: 'danger',
          summary: 'Erreur',
          detail: `Il y a eu une erreur lors de l'ajout de ${newMemberEmail}`,
          life: 3000
        })
      }
    },
    async removeMember({ value }) {
      const emailOfMemberToRemove = value[0]

      /**
       * Search if the email belongs to a member
       * if not, search in the invitations and set isAMember to false
       * */
      let memberToRemove = this.project.members.filter(member => member.email === emailOfMemberToRemove)[0]
      let isAMember = true

      if (!memberToRemove) {
        isAMember = false
        memberToRemove = this.project.invitations.filter(member => member.email === emailOfMemberToRemove)[0]
      }

      /** If the email does not match any members or invitations an error toast is displayed */
      if (!memberToRemove) {
        return this.$toast.add({
          severity: 'danger',
          summary: 'Erreur',
          detail: `L'adresse email ${emailOfMemberToRemove} n'appartient à aucun membre du projet`,
          life: 5000
        })
      }
      try {
        const apiRoute = isAMember
          ? `/projects/${this.$route.params.idProject}/member/${memberToRemove.id}`
          : `/project_invitations/${memberToRemove.id}`

        await axios.delete(apiRoute)
        this.$toast.add({
          severity: 'success',
          summary: 'Membre supprimé',
          detail: `Le membre a été supprimé du projet`,
          life: 3000
        })
      } catch ({ response }) {
        this.$toast.add({
          severity: 'danger',
          summary: 'Erreur',
          detail: `Il y a eu une erreur lors de la suppression du membre`,
          life: 5000
        })
      }
    },
    parseErrorRule(error, fieldWording) {
      if (error.find(rule => rule === "validation.required")) {
        return `${fieldWording} est obligatoire`
      }
      if (error.find(rule => rule === "validation.string.max")) {
        return `${fieldWording} est trop long`
      }
      if (error.find(rule => rule === "validation.string.min")) {
        return `${fieldWording} est trop court`
      }
      if (error.find(rule => rule === "validation.string")) {
        return `${fieldWording} doit être du texte`
      }
    },
  },
  watch: {
    "project.startDate": function(newStartDate) {
      // If the new start date is later than the endDate, endDate is reset.
      if(this.project.endDate && moment(newStartDate).isAfter(this.project.endDate)) {
        this.project.endDate = null
      }
    },
  },
}
</script>
<style lang="scss">
.label-choice .card-ui {
  min-height: 250px;
}

#step1, #step2 {
  transition-property: left, right;
  transition-duration: 1s, 1s;
  right: 0%;
  width: 100%
}

#step2 {
  position: absolute;
  top: 0;
  width: 100%;
  background: var(--bg-lighter);
}

.currentStep {
  position: absolute;
  right: 0%;
}

#step1.hiddenStep {
  position: absolute;
  right: 100%;
}

#step2.hiddenStep {
  position: absolute;
  right: -100%;
}

input[type="radio"] + label span, input[type="checkbox"] + label span {
  transition: all .2s,
  transform .2s;
}

input[type="radio"] + label span:hover,
input[type="radio"] + label:hover span,
input[type="checkbox"] + label span:hover,
input[type="checkbox"] + label:hover span {
  transform: scale(1.1);
}

input[type="radio"]:checked + label span, input[type="checkbox"]:checked + label span {
  background-color: var(--primary); //bg-blue
  box-shadow: 0px 0px 0px 5px white inset;
}

input[type="radio"]:checked + label, input[type="checkbox"]:checked + label {
  color: #3490DC; //text-blue
}
</style>
